import type { AxiosError, AxiosResponse } from 'axios';
import type { ApiError, ApiResult } from './types';

interface RawBody {
  detail?: unknown;
  codigo?: string;
  code?: string;
  message?: string;
  title?: string;
  errores?: Record<string, string | string[]>;
  errors?: Record<string, string | string[]>;
  datos?: unknown;
}

const joinMsg = (v: string | string[]): string => (Array.isArray(v) ? v.join(' ') : String(v));

/** FastAPI 422 clásico: detail = [{loc:["body","email"], msg, type}] → { email: msg } */
function fieldErrorsFromDetail(detail: unknown): Record<string, string> | undefined {
  if (!Array.isArray(detail)) return undefined;
  const out: Record<string, string> = {};
  for (const item of detail) {
    if (item && typeof item === 'object' && 'loc' in item && 'msg' in item) {
      const loc = (item as { loc: unknown[] }).loc;
      const field = loc.filter((p) => p !== 'body' && p !== 'query' && p !== 'path').join('.');
      out[field || '_'] = String((item as { msg: unknown }).msg);
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function detailText(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: unknown } | undefined;
    return first?.msg ? String(first.msg) : undefined;
  }
  return undefined;
}

export function mapDefaultTitle(status: number): string {
  if (status === 0) return 'errors.networkTitle';
  if (status === 401) return 'errors.unauthorizedTitle';
  if (status === 403) return 'errors.forbiddenTitle';
  if (status === 404) return 'errors.notFoundTitle';
  if (status === 409) return 'errors.conflictTitle';
  if (status === 422) return 'errors.validationTitle';
  if (status === 429) return 'errors.rateLimitTitle';
  if (status >= 500) return 'errors.serverTitle';
  return 'errors.genericTitle';
}

export function mapDefaultDetail(status: number): string {
  if (status === 0) return 'errors.networkDetail';
  if (status === 401) return 'errors.unauthorizedDetail';
  if (status === 403) return 'errors.forbiddenDetail';
  if (status === 404) return 'errors.notFoundDetail';
  if (status === 409) return 'errors.conflictDetail';
  if (status === 422) return 'errors.validationDetail';
  if (status === 429) return 'errors.rateLimitDetail';
  if (status >= 500) return 'errors.serverDetail';
  return 'errors.genericDetail';
}

export function handleErrorAxios(error: AxiosError<RawBody>): ApiError {
  const res = error.response;
  if (!res) {
    return { ok: false, status: 0, title: mapDefaultTitle(0), detail: mapDefaultDetail(0) };
  }
  const body: RawBody = res.data && typeof res.data === 'object' ? res.data : {};
  const explicit = body.errores ?? body.errors;
  const fieldErrors = explicit
    ? Object.fromEntries(Object.entries(explicit).map(([k, v]) => [k, joinMsg(v)]))
    : fieldErrorsFromDetail(body.detail);
  return {
    ok: false,
    status: res.status,
    title: body.title || mapDefaultTitle(res.status),
    // El `detail` del servidor se muestra tal cual (regla 422 del skill).
    detail: detailText(body.detail) || body.message || mapDefaultDetail(res.status),
    code: body.codigo ?? body.code,
    fieldErrors,
    extra: body.datos,
  };
}

export function badResponse(detail = 'errors.badResponseDetail'): ApiError {
  return {
    ok: false,
    status: 0,
    title: 'errors.badResponseTitle',
    detail,
    code: 'respuesta_invalida',
  };
}

/**
 * Convierte una petición axios en ApiResult<T>. Nunca lanza.
 * `parse` valida/normaliza el cuerpo (p. ej. con zod); si lanza, devuelve badResponse.
 */
export async function toResult<T>(
  request: Promise<AxiosResponse<unknown>>,
  parse: (data: unknown) => T = (d) => d as T,
): Promise<ApiResult<T>> {
  try {
    const res = await request;
    try {
      const headers: Record<string, string> = {};
      for (const k of ['etag', 'last-modified', 'x-total-count']) {
        const v = res.headers?.[k];
        if (typeof v === 'string') headers[k] = v;
      }
      return { ok: true, data: parse(res.data), headers };
    } catch (e) {
      if (import.meta.env.DEV) console.error('[api] respuesta con formato inesperado', e);
      return badResponse();
    }
  } catch (e) {
    return handleErrorAxios(e as AxiosError<RawBody>);
  }
}

export function isSuccessfully(status: string | number | undefined | null): boolean {
  if (typeof status === 'number') return status >= 200 && status < 300;
  return status === 'OK' || status === 'ok';
}

/** Arma una query string omitiendo vacíos. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}
